<script lang="ts">
  import { push, querystring, location } from "svelte-spa-router";
  import { filteringDate } from "../helpers/store";
  import { createGetSubscriptionQuery } from "../queries/subscriptions";
  import ButtonNew from "../components/buttons/ButtonNew.svelte";
  import InvoiceDataGrid from "../components/invoices/InvoiceDataGrid.svelte";
  import PageHeaderNew from "../components/headers/PageHeaderNew.svelte";
  import { globalDatePickerStore } from "../stores/date-picker";
  import DatePickerPanel from "../components/date-picker/DatePickerPanel.svelte";
  import { DateFilterType } from "../helpers/types";
  import OverviewCard from "../components/invoices/OverviewCard.svelte";
  import { createSpendReportQuery } from "../queries/spend-report";
  import { formatSpendReportTotals } from "../derived-data/spend-report";
  import LinkTransactionsModal from "../components/modals/LinkTransactionsModal.svelte";
  import ButtonGroup from "../components/spend-report/ButtonGroup.svelte";
  import RadioButton from "../components/RadioButton.svelte";
  import {
    CHAT_FILTER_STORAGE_KEY,
    CHAT_FILTER_VERSION,
    CONTENT_PADDING_X,
    EXPORT_CONTEXT_MENU_MIN_WIDTH,
    GROUP_TYPE_FILTERS,
    INVOICE_PAGE_VERTICAL_GAP,
    INVOICE_PAGE_VERTICAL_PADDING,
    LINK_STATUS_FILTER_PARAM_KEY,
  } from "../components/invoices/constants";
  import { createModal } from "../components/modals/createModal";
  import { hasNoSubscription } from "../helpers/subscription";
  import { subscriptionModal } from "../stores/subscription-modal-store";
  import InvoiceUploadModal from "../components/invoices/InvoiceUploadModal.svelte";
  import {
    createExportCSVTransactionsMutation,
    createExportPDFInvoicesMutation,
  } from "../mutations/transactions";
  import ContextMenu from "../components/invoices/ContextMenu.svelte";
  import { createDropdownMenu, type MenuParams } from "../components/dropdown-menu/create";
  import ExportMenuItem from "../components/invoices/ExportMenuItem.svelte";
  import {
    invoicesPageHeaderHeight,
    invoicesPageOverviewHeight,
  } from "../components/invoices/stores/dimensions";
  import { extractLinkStatusParam } from "../helpers/url";
  import { TAX_TYPE_MAP, currency, currencyFormatter } from "../helpers/localise";
  import { createPersistedLocalStore } from "../stores/persisted-local-store";
  import { selectedBuyer } from "../stores/buyer";

  $: buyerID = $selectedBuyer?.id ?? "";
  $: filterDate = $filteringDate;
  $: startDate = $globalDatePickerStore.confirmedStartDate.toISOString();
  $: endDate = $globalDatePickerStore.confirmedEndDate.toISOString();

  $: subscriptionQuery = createGetSubscriptionQuery(buyerID);
  $: linkStatus = extractLinkStatusParam($querystring);

  const exportTransactionsMutation = createExportCSVTransactionsMutation();
  const exportInvoicesMutation = createExportPDFInvoicesMutation();

  const dateRangeFilters = [
    DateFilterType.Past7Days,
    DateFilterType.Past14Days,
    DateFilterType.ThisMonth,
  ];

  const { underlayAttrs, contentAttrs, open, triggerElement, ...modal } = createModal();

  const {
    open: isExportMenuOpen,
    menu: exportMenu,
    onTriggerClick: onExportMenuTriggerClick,
    onMenuClose: onExportMenuClose,
    menuAttrs: exportMenuAttrs,
    triggerAttrs: exportMenuTriggerAttrs,
    triggerElement: exportMenuTriggerElement,
  } = createDropdownMenu();

  const exportMenuOptions: MenuParams = {
    strategy: "absolute",
    placement: "bottom",
    closeOnClickOutside: true,
    offset: 8,
  };

  const chatFiltersByID = createPersistedLocalStore<Record<string, boolean>>(
    CHAT_FILTER_STORAGE_KEY,
    {},
    CHAT_FILTER_VERSION
  );

  $: chatIDs = Object.entries($chatFiltersByID)
    .filter(([, isSelected]) => isSelected)
    .map(([id]) => id);

  $: spendReportQuery = createSpendReportQuery(buyerID, startDate, endDate, filterDate, chatIDs);
  $: spendReportTotals = formatSpendReportTotals($spendReportQuery, $currencyFormatter);

  function handleUploadInvoiceClick() {
    if (!$subscriptionQuery.data) {
      return;
    }

    if (hasNoSubscription($subscriptionQuery.data.plan, $subscriptionQuery.data.status)) {
      subscriptionModal.open("invoice-upload");
    } else {
      modal.onOpen();
    }
  }

  function handleExportCSVClick() {
    $exportTransactionsMutation.mutate({
      buyer_id: buyerID,
      start_date: $globalDatePickerStore.confirmedStartDate.toISOString(),
      end_date: $globalDatePickerStore.confirmedEndDate.toISOString(),
      filtering_date: $filteringDate,
    });

    onExportMenuClose();
  }

  function handleExportPDFClick() {
    $exportInvoicesMutation.mutate({
      buyer_id: buyerID,
      start_date: $globalDatePickerStore.confirmedStartDate.toISOString(),
      end_date: $globalDatePickerStore.confirmedEndDate.toISOString(),
      filtering_date: $filteringDate,
      chat_ids: chatIDs,
    });
    onExportMenuClose();
  }
</script>

<div
  class="container"
  style:--content-px={CONTENT_PADDING_X}
  style:--page-vertical-gap={INVOICE_PAGE_VERTICAL_GAP}
  style:--page-vertical-padding={INVOICE_PAGE_VERTICAL_PADDING}
>
  <PageHeaderNew title="invoices" bind:height={$invoicesPageHeaderHeight}>
    <div class="left" slot="left-aligned">
      <ButtonGroup>
        {#each GROUP_TYPE_FILTERS as t}
          <RadioButton
            on:click={() => {
              if (!$querystring) {
                return;
              }

              const queryParams = new URLSearchParams($querystring);
              if (queryParams.get(LINK_STATUS_FILTER_PARAM_KEY) !== t) {
                queryParams.set(LINK_STATUS_FILTER_PARAM_KEY, t);
                push(`${$location}?${queryParams.toString()}`);
              }
            }}
            isActive={linkStatus === t}
          >
            {t}
          </RadioButton>
        {/each}
      </ButtonGroup>
      <DatePickerPanel {dateRangeFilters} datePickerStore={globalDatePickerStore} />
    </div>

    <div class="right" slot="right-aligned">
      <ButtonNew
        {...exportMenuTriggerAttrs}
        size="xs"
        isLoading={$exportTransactionsMutation.isPending || $exportInvoicesMutation.isPending}
        disabled={$exportTransactionsMutation.isPending || $exportInvoicesMutation.isPending}
        on:click={onExportMenuTriggerClick}
        bind:element={$exportMenuTriggerElement}
        --bg="var(--rk-neutral-4)"
        --minW="76.5px"
      >
        export
      </ButtonNew>
      <ButtonNew size="xs" on:click={handleUploadInvoiceClick} bind:element={$triggerElement}>
        upload
      </ButtonNew>
    </div>
  </PageHeaderNew>

  <main>
    <div class="overview" bind:clientHeight={$invoicesPageOverviewHeight}>
      <OverviewCard
        variant="net"
        isLoading={!spendReportTotals}
        total={spendReportTotals?.totalNet}
        invoiceTotal={spendReportTotals?.invoiceNet}
        creditNoteTotal={spendReportTotals?.creditNoteNet}
      />

      <OverviewCard
        variant="gross"
        isLoading={!spendReportTotals}
        total={spendReportTotals?.totalGross}
        invoiceTotal={spendReportTotals?.invoiceGross}
        creditNoteTotal={spendReportTotals?.creditNoteGross}
      />

      <OverviewCard
        variant={TAX_TYPE_MAP[$currency]}
        isLoading={!spendReportTotals}
        total={spendReportTotals?.totalVAT}
        invoiceTotal={spendReportTotals?.invoiceVAT}
        creditNoteTotal={spendReportTotals?.creditNoteVAT}
      />
    </div>

    <InvoiceDataGrid {chatFiltersByID} />
    <LinkTransactionsModal />
  </main>
</div>

<InvoiceUploadModal
  contentAction={modal.content}
  contentAttrs={$contentAttrs}
  underlayAttrs={$underlayAttrs}
  open={$open}
  onClose={modal.onClose}
/>

{#if $isExportMenuOpen}
  <ContextMenu
    {...$exportMenuAttrs}
    action={exportMenu}
    menuOptions={exportMenuOptions}
    --filter-menu-minW={EXPORT_CONTEXT_MENU_MIN_WIDTH}
  >
    <ExportMenuItem on:click={handleExportCSVClick}>.csv with all the data</ExportMenuItem>
    <ExportMenuItem on:click={handleExportPDFClick}>.pdf invoices only</ExportMenuItem>
  </ContextMenu>
{/if}

<style>
  .container {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    background-color: white;
    height: 100vh;
    padding: var(--page-vertical-padding) var(--content-px);
  }

  main {
    height: 100%;
  }

  .left {
    display: flex;
  }

  .right {
    display: flex;
    gap: 4px;
  }

  .overview {
    display: flex;
    gap: 12px;
    margin-top: var(--page-vertical-gap);
  }
</style>
